requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    scriptController.executeWhenScriptLoaded(['modal-opener.js'], () => {
      if (!customElements.get('product-modal')) {
        customElements.define(
          'product-modal',
          class ProductModal extends ModalDialog {
            constructor() {
              super();
              this.historyEntryActive = false;
              this.historyToken = null;
              this.pendingCloseCallback = null;
              this.closing = false;
              this.opening = false;
              this.modalWasOpened = false;
              this.historyCloseFallback = null;
              this.popStateHandler = this.handlePopState.bind(this);
              this.historyListenerBound = false;
              this.addEventListener('click', this.handleBackdropClick.bind(this));
              this.addEventListener('modalOpened', this.handleModalOpened.bind(this));
              this.addEventListener('modalClosed', this.handleModalClosed.bind(this));
            }

            connectedCallback() {
              if (this.historyListenerBound) return;
              window.addEventListener('popstate', this.popStateHandler);
              this.historyListenerBound = true;
            }

            disconnectedCallback() {
              if (this.historyListenerBound) {
                window.removeEventListener('popstate', this.popStateHandler);
                this.historyListenerBound = false;
              }

              if (
                this.historyEntryActive &&
                history.state?.pmcProductMediaModal === this.historyToken
              ) {
                const currentState = { ...history.state };
                delete currentState.pmcProductMediaModal;
                history.replaceState(currentState, '', window.location.href);
              }

              this.historyEntryActive = false;
              this.historyToken = null;
              this.pendingCloseCallback = null;
              this.closing = false;
              this.opening = false;
              this.modalWasOpened = false;
              clearTimeout(this.historyCloseFallback);
              this.historyCloseFallback = null;

              if (this.hasAttribute('open')) {
                document.body.removeEventListener('keyup', this.onEscapeHandler);
                removeTrapFocus(this.openedBy);
                themeBase.unfreezeScroll({
                  additionalClass: this.freezeClasslist
                });
                themeBase.toogleClasslist(document.body, this.bodyClasslist, 'remove');
                this.removeAttribute('open');
              }
            }

            hide(callback) {
              if (this.closing) return;

              const onClosed = typeof callback === 'function' ? callback : undefined;
              const ownsCurrentHistoryEntry =
                this.historyEntryActive &&
                history.state?.pmcProductMediaModal === this.historyToken;

              this.closing = true;

              if (ownsCurrentHistoryEntry) {
                this.pendingCloseCallback = onClosed;
                history.back();
                clearTimeout(this.historyCloseFallback);
                this.historyCloseFallback = setTimeout(() => {
                  if (!this.closing || !this.historyEntryActive) return;

                  const currentState =
                    history.state && typeof history.state === 'object'
                      ? { ...history.state }
                      : {};
                  delete currentState.pmcProductMediaModal;
                  history.replaceState(currentState, '', window.location.href);

                  this.historyEntryActive = false;
                  this.finishHide(this.pendingCloseCallback);
                  this.pendingCloseCallback = null;
                }, 600);
                return;
              }

              this.historyEntryActive = false;
              this.historyToken = null;
              this.pendingCloseCallback = null;
              this.finishHide(onClosed);
            }

            show(opener) {
              if (
                this.opening ||
                this.closing ||
                this.historyEntryActive ||
                this.hasAttribute('open') ||
                (window.ModalDialogOpened?.modal && window.ModalDialogOpened.modal !== this)
              )
                return;

              this.opening = true;
              super.show(opener);
              this.showActiveMedia();
            }

            pushHistoryEntry() {
              const currentState =
                history.state && typeof history.state === 'object'
                  ? history.state
                  : {};

              try {
                this.historyToken = `${this.id || 'product'}:${Date.now()}:${performance.now()}`;
                history.pushState(
                  {
                    ...currentState,
                    pmcProductMediaModal: this.historyToken
                  },
                  '',
                  window.location.href
                );
                this.historyEntryActive = true;
              } catch (error) {
                this.historyEntryActive = false;
                this.historyToken = null;
              }
            }

            handlePopState() {
              if (!this.historyEntryActive) return;
              if (history.state?.pmcProductMediaModal === this.historyToken) return;

              clearTimeout(this.historyCloseFallback);
              this.historyCloseFallback = null;
              this.historyEntryActive = false;
              const onClosed = this.pendingCloseCallback;
              this.pendingCloseCallback = null;

              if (!this.modalWasOpened && !this.hasAttribute('open')) {
                this.closing = false;
                this.historyToken = null;
                return;
              }

              this.closing = true;
              this.finishHide(onClosed);
            }

            finishHide(callback) {
              const opener = this.openedBy;
              super.hide(() => {
                removeTrapFocus(opener);
                opener?.focus?.({ preventScroll: true });
                if (typeof callback === 'function') callback();
              });
            }

            handleBackdropClick(event) {
              const dialog = this.querySelector('.product-media-modal__dialog');
              const content = this.querySelector('.product-media-modal__content');

              if (event.target === this || event.target === dialog || event.target === content) {
                this.hide();
              }
            }

            handleModalOpened() {
              this.opening = false;
              this.modalWasOpened = true;
              if (!this.historyEntryActive) this.pushHistoryEntry();
              const dialog = this.querySelector('.product-media-modal__dialog');
              const closeButton = this.querySelector('[id^="ModalClose-"]');
              if (dialog && closeButton) trapFocus(dialog, closeButton);
            }

            handleModalClosed() {
              this.closing = false;
              this.opening = false;
              this.historyToken = null;
              this.modalWasOpened = false;
              clearTimeout(this.historyCloseFallback);
              this.historyCloseFallback = null;
              removeTrapFocus(this.openedBy);
            }

            showActiveMedia() {
              const mediaId = this.openedBy?.getAttribute('data-media-id');
              if (!mediaId) return;

              this.querySelectorAll(
                `[data-media-id]:not([data-media-id="${mediaId}"])`
              ).forEach((element) => {
                element.classList.remove('active');
              });

              const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
              if (!activeMedia) return;

              const activeMediaTemplate = activeMedia.querySelector('template');
              const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
              activeMedia.classList.add('active');
              activeMedia.scrollIntoView();

              const container = this.querySelector('[role="document"]');
              if (container) {
                setLogicalScrollLeft(container, (activeMedia.clientWidth - container.clientWidth) / 2);
              }

              if (
                activeMedia.nodeName == 'DEFERRED-MEDIA' &&
                activeMediaContent &&
                activeMediaContent.querySelector('.js-youtube') &&
                typeof activeMedia.loadContent === 'function'
              )
                activeMedia.loadContent();
            }
          }
        );

        scriptController.scripts.push('product-modal.js');
      }
    });
  });
});
